import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import "./App.css";
import GlobalSnackbar from "./components/GlobalSnackbar";
import { BrowserRouter } from "react-router-dom";
import AppRoutes from "./router";
import Contexts from "./contexts/Contexts";
import GlobalMessage from "./components/GlobalMessage";
import { setCookie } from "./utils/cookieUtils";
import { resolveAffiliateCookies } from "./utils/affiliateCookies";
import { clearUserCache } from "./utils/userCacheUtils";
import { useStorefront } from "./hooks/useStorefront";
import CartAddAnimation from "./components/CartAddAnimation";

// Lazy load non-critical components to improve FCP
const Login = lazy(() => import("./components/Registration/Login"));
const Registration = lazy(() => import("./components/Registration"));
const PublicDrawer = lazy(() => import("./components/PublicDrawer"));

// 获取版本号
const APP_VERSION = process.env.REACT_APP_VERSION || require('../package.json').version;

interface User {
  id: number;
  name: string;
  surname?: string;
  phoneNumber?: string;
  avatar?: string;
  isGuest?: boolean; // 标识是否为访客用户
  giftingEnabled?: boolean; // Gifting Flavor A dark-gate (see Contexts User)
}

function App() {
  const storefront = useStorefront();
  const [screenSize, setScreenSize] = useState({
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
  });

  useEffect(() => {
    // Use requestAnimationFrame for smoother resize handling
    let rafId: number | null = null;
    
    const handleResize = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        setScreenSize({
          isMobile: window.innerWidth < 768,
          isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
          isDesktop: window.innerWidth >= 1024,
        });
      });
    };

    // Throttle resize events to reduce blocking time
    let timeoutId: NodeJS.Timeout;
    const throttledResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", throttledResize, { passive: true });
    return () => {
      window.removeEventListener("resize", throttledResize);
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, []);

  const [isNavOpen, setIsNavOpen] = useState(true);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [cartCount, setCartCount] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [appLoading, setAppLoading] = useState(true); // Start with true
  // Flips once GET /api/user/info/ has SETTLED (any outcome). Distinguishes "the server
  // said gifting is off" from "we haven't asked yet" — see Contexts.userInfoResolved.
  const [userInfoResolved, setUserInfoResolved] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [publicDrawerKey, setPublicDrawerKey] = useState("");
  const [drawerData, setDrawerData] = useState<any>(null);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [globalGameFilter, setGlobalGameFilter] = useState<any>(null);
  const [globalMessage, setGlobalMessageState] = useState("");
  const [globalMessageColor, setGlobalMessageColor] = useState<"success" | "danger">("success");
  // Toasts default to success so existing callers are unchanged; error callers pass "danger".
  // Only set the color for a real message — clearing ("") must not flip a dying danger
  // toast back to green during its ~300ms close animation.
  const setGlobalMessage = useCallback(
    (message: string, color: "success" | "danger" = "success") => {
      if (message) {
        setGlobalMessageColor(color);
      }
      setGlobalMessageState(message);
    },
    [],
  );
  const [globalMessageAction, setGlobalMessageAction] = useState<(() => void) | null>(null);
  const [postLoginRedirect, setPostLoginRedirect] = useState<string | null>(null);
  
  // Bundle 7D: session-expired handler — fires when backend returns 401
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      setUserId(null);
      clearUserCache();
      setIsLoginOpen(true);
    };
    window.addEventListener('lvs:session-expired', handleSessionExpired);
    return () => window.removeEventListener('lvs:session-expired', handleSessionExpired);
  }, []);

  // 应用启动时输出版本号 - 延迟到空闲时间执行
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        console.log(APP_VERSION);
      });
    } else {
      setTimeout(() => console.log(APP_VERSION), 0);
    }
  }, []);

  // Welcome 界面展示逻辑已注释
  // useEffect(() => {
  //   // 只有在完全没有用户时显示Welcome界面，访客用户不显示
  //   // 当user为null时显示，当user存在时（无论是真实用户还是访客）都不显示
  //   if (!appLoading && !user) {
  //     setIsDrawerOpen(true);
  //     setPublicDrawerKey("Welcome");
  //   } else if (!appLoading && user?.isGuest) {
  //     // 如果是访客用户，确保关闭Welcome抽屉
  //     setIsDrawerOpen(false);
  //   }
  // }, [appLoading, user]);

  // 检测URL参数并存储到cookie - 延迟执行避免阻塞
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const fromParam = urlParams.get('from');
        
        if (fromParam === 'ads') {
          // 将广告来源信息存储到cookie，有效期30天
          setCookie('ad_source', 'ads', 30);
          console.log('广告来源已存储到cookie');
        }

        // Affiliate ref token — store for 30 days, keep URL as-is for resharing
        const refToken = urlParams.get('ref');
        if (refToken) {
          setCookie('lvs_ref', refToken, 30);
          localStorage.setItem('lvs_ref', refToken);
        }

        // Bundle 7.B Hotfix #2 — resolves house vs sub, sets aff_house + aff_sub correctly
        const affCode = urlParams.get('aff');
        if (affCode) {
          resolveAffiliateCookies(affCode);
        }
      });
    } else {
      setTimeout(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const fromParam = urlParams.get('from');
        
        if (fromParam === 'ads') {
          setCookie('ad_source', 'ads', 30);
          console.log('广告来源已存储到cookie');
        }

        // Affiliate ref token — store for 30 days, keep URL as-is for resharing
        const refToken = urlParams.get('ref');
        if (refToken) {
          setCookie('lvs_ref', refToken, 30);
          localStorage.setItem('lvs_ref', refToken);
        }

        // Bundle 7.B Hotfix #2 — resolves house vs sub, sets aff_house + aff_sub correctly
        const affCode = urlParams.get('aff');
        if (affCode) {
          resolveAffiliateCookies(affCode);
        }
      }, 0);
    }
  }, []);

  return (
    <>
      <Contexts.Provider
        value={{
          globalMessage,
          globalMessageColor,
          setGlobalMessage,
          globalMessageAction,
          setGlobalMessageAction,
          appLoading,
          setAppLoading,
          storefront,
          // Raw server value, passed through untouched. The build-time AND and the
          // not-yet-resolved case both live in useGiftingGate() — never gate on this directly.
          giftingEnabled: user?.giftingEnabled === true,
          userInfoResolved,
          setUserInfoResolved,
          isNavOpen,
          setNavVisible: setIsNavOpen,
          isLoginOpen,
          setIsLoginOpen,
          user,
          setUser,
          isRegistrationOpen,
          setIsRegistrationOpen,
          userId,
          setUserId,
          cartCount,
          setCartCount,
          setShowFilter,
          showFilter,
          isDrawerOpen,
          setIsDrawerOpen,
          publicDrawerKey,
          setPublicDrawerKey,
          notificationsCount,
          setNotificationsCount,
          drawerData,
          setDrawerData,
          screenSize,
          setErrorMessage,
          errorMessage,
          globalGameFilter,
          setGlobalGameFilter,
          postLoginRedirect,
          setPostLoginRedirect,
        }}
      >
        <BrowserRouter>
          <div
            style={{
              paddingTop: showFilter ? "40px" : 0,
            }}
          >
            <AppRoutes />
          </div>
          <GlobalSnackbar error={errorMessage} />
          <GlobalMessage />
          <CartAddAnimation />

          <Suspense fallback={null}>
            {isLoginOpen && (
              <Login
                handleOnClose={() => setIsLoginOpen(false)}
                onSubmit={() => {}}
              />
            )}
            {isRegistrationOpen && (
              <Registration
                onClose={() => setIsRegistrationOpen(false)}
                onSubmit={() => {}}
              />
            )}
            {publicDrawerKey && (
              <PublicDrawer
                key={publicDrawerKey}
                open={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
              />
            )}
          </Suspense>
        </BrowserRouter>
      </Contexts.Provider>
    </>
  );
}
export default App;
